import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IssuanceReview = ({ selectedTemplate, formData, batchData, onIssue, onBack }) => {
  const [isIssuing, setIsIssuing] = useState(false);
  const [issuanceProgress, setIssuanceProgress] = useState({ current: 0, total: 0, operation: '' });
  const [cryptographicKeys, setCryptographicKeys] = useState(null);
  const [blockchainStatus, setBlockchainStatus] = useState('ready');

  const isBatchMode = batchData && batchData?.length > 0;
  const totalCredentials = isBatchMode ? batchData?.length : 1;

  useEffect(() => {
    // Simulate cryptographic key generation
    const generateKeys = async () => {
      setCryptographicKeys({
        publicKey: 'did:key:z6MkhaXgBZDvotDkL5257faiztiGiC2QtKLGpbnnEGta2doK',
        keyId: 'key-1',
        algorithm: 'Ed25519',
        created: new Date()?.toISOString()
      });
    };

    generateKeys();
  }, []);

  const handleIssue = async () => {
    setIsIssuing(true);
    
    try {
      // Simulate issuance process
      const operations = [
        'Validating credential data',
        'Generating cryptographic signatures',
        'Creating zero-knowledge proofs',
        'Anchoring to blockchain',
        'Finalizing credentials'
      ];

      for (let i = 0; i < operations?.length; i++) {
        setIssuanceProgress({
          current: i + 1,
          total: operations?.length,
          operation: operations?.[i]
        });
        
        // Simulate processing time
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Complete issuance
      onIssue({
        success: true,
        credentialsIssued: totalCredentials,
        transactionHash: '0x' + Math.random()?.toString(16)?.substr(2, 64),
        timestamp: new Date()?.toISOString()
      });
      
    } catch (error) {
      console.error('Issuance failed:', error);
    } finally {
      setIsIssuing(false);
    }
  };

  const renderCredentialSummary = () => {
    if (isBatchMode) {
      return (
        <div className="space-y-4">
          <h4 className="font-medium text-foreground">Batch Credentials Summary</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-primary">{batchData?.length}</div>
              <div className="text-sm text-muted-foreground">Total Credentials</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-success">{selectedTemplate?.name}</div>
              <div className="text-sm text-muted-foreground">Template Type</div>
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {batchData?.slice(0, 5)?.map((record, index) => (
              <div key={index} className="flex justify-between text-sm p-2 bg-muted/50 rounded">
                <span className="text-foreground">{record?.recipient_name}</span>
                <span className="text-muted-foreground">{record?.recipient_email}</span>
              </div>
            ))}
            {batchData?.length > 5 && (
              <div className="text-center text-sm text-muted-foreground">
                ... and {batchData?.length - 5} more recipients
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h4 className="font-medium text-foreground">Single Credential Summary</h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Recipient:</span>
            <span className="text-foreground">{formData?.recipient_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Email:</span>
            <span className="text-foreground">{formData?.recipient_email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Template:</span>
            <span className="text-foreground">{selectedTemplate?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Format:</span>
            <span className="text-foreground">{formData?.format}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack} disabled={isIssuing}>
          <Icon name="ArrowLeft" size={20} />
        </Button>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Review & Issue Credentials</h2>
          <p className="text-muted-foreground mt-1">
            Final review before issuing {totalCredentials} credential{totalCredentials > 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Review Section */}
        <div className="space-y-6">
          {/* Credential Summary */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="FileText" size={20} className="mr-2" />
              Credential Details
            </h3>
            {renderCredentialSummary()}
          </div>

          {/* Security Configuration */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Shield" size={20} className="mr-2" />
              Security Configuration
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Digital Signature:</span>
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-success text-sm">Ready</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Zero-Knowledge Proofs:</span>
                <div className="flex items-center gap-2">
                  <Icon name={formData?.enable_zkp ? "CheckCircle" : "XCircle"} size={16} 
                        className={formData?.enable_zkp ? "text-success" : "text-muted-foreground"} />
                  <span className={`text-sm ${formData?.enable_zkp ? "text-success" : "text-muted-foreground"}`}>
                    {formData?.enable_zkp ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Blockchain Anchoring:</span>
                <div className="flex items-center gap-2">
                  <Icon name={formData?.blockchain_anchor ? "CheckCircle" : "XCircle"} size={16} 
                        className={formData?.blockchain_anchor ? "text-success" : "text-muted-foreground"} />
                  <span className={`text-sm ${formData?.blockchain_anchor ? "text-success" : "text-muted-foreground"}`}>
                    {formData?.blockchain_anchor ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Revocation Policy:</span>
                <span className="text-foreground text-sm capitalize">
                  {formData?.revocation_policy?.replace('_', ' ') || 'Issuer Only'}
                </span>
              </div>
            </div>
          </div>

          {/* Cryptographic Keys */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Key" size={20} className="mr-2" />
              Cryptographic Keys
            </h3>
            {cryptographicKeys ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Algorithm:</span>
                  <span className="text-foreground font-mono text-sm">{cryptographicKeys?.algorithm}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Key ID:</span>
                  <span className="text-foreground font-mono text-sm">{cryptographicKeys?.keyId}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground">Public Key:</span>
                  <span className="text-foreground font-mono text-xs break-all bg-muted p-2 rounded">
                    {cryptographicKeys?.publicKey}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-success">
                  <Icon name="CheckCircle" size={16} />
                  <span className="text-sm">Keys generated and verified</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span className="text-sm">Generating cryptographic keys...</span>
              </div>
            )}
          </div>
        </div>

        {/* Status Section */}
        <div className="space-y-6">
          {/* Issuance Progress */}
          {isIssuing && (
            <div className="credential-card">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Icon name="Zap" size={20} className="mr-2" />
                Issuance Progress
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{issuanceProgress?.operation}</span>
                  <span className="text-foreground">{issuanceProgress?.current}/{issuanceProgress?.total}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(issuanceProgress?.current / issuanceProgress?.total) * 100}%` }}
                  />
                </div>
                <div className="flex items-center gap-2 text-primary">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span className="text-sm">Processing credentials...</span>
                </div>
              </div>
            </div>
          )}

          {/* System Status */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Activity" size={20} className="mr-2" />
              System Status
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Blockchain Network:</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                  <span className="text-success text-sm">Connected</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Gas Price:</span>
                <span className="text-foreground text-sm">12 Gwei</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Estimated Cost:</span>
                <span className="text-foreground text-sm">${(totalCredentials * 0.02)?.toFixed(2)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Processing Time:</span>
                <span className="text-foreground text-sm">~{Math.ceil(totalCredentials * 0.5)}s</span>
              </div>
            </div>
          </div>

          {/* Final Checklist */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="CheckSquare" size={20} className="mr-2" />
              Pre-Issuance Checklist
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-foreground">Credential data validated</span>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-foreground">Cryptographic keys ready</span>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-foreground">Blockchain connection active</span>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-foreground">Security policies configured</span>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-foreground">Recipients verified</span>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="DollarSign" size={20} className="mr-2" />
              Cost Breakdown
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Blockchain fees:</span>
                <span className="text-foreground">${(totalCredentials * 0.015)?.toFixed(3)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Processing fees:</span>
                <span className="text-foreground">${(totalCredentials * 0.005)?.toFixed(3)}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-medium">
                <span className="text-foreground">Total:</span>
                <span className="text-foreground">${(totalCredentials * 0.02)?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button variant="outline" onClick={onBack} disabled={isIssuing}>
          Back to {isBatchMode ? 'Batch' : 'Form'}
        </Button>
        <Button 
          onClick={handleIssue}
          loading={isIssuing}
          disabled={!cryptographicKeys}
          iconName="Zap" 
          iconPosition="left"
        >
          {isIssuing ? 'Issuing...' : `Issue ${totalCredentials} Credential${totalCredentials > 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
};

export default IssuanceReview;